import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { Avatar, Box } from '@mui/material';

// Supabase Image Avatar component for landing page profile cards
const SupabaseImageAvatar = React.memo(({ 
  user, 
  size = 80, 
  sx = {},
  refreshKey = 0 // Optional refresh key for cache-busting
}) => {
  const [hasError, setHasError] = useState(false);

  // Reset error state when user changes or refresh key changes
  useEffect(() => {
    setHasError(false);
  }, [user?.studentID, refreshKey]);

  // Generate Supabase image URL with memoization to prevent re-computation
  const imageSrc = useMemo(() => {
    const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
    const imagePath = process.env.REACT_APP_SUPABASE_IMAGE_URL;
    
    if (!supabaseUrl || !imagePath || !user?.studentID) {
      return null;
    }

    // Construct the full URL: REACT_APP_SUPABASE_URL + REACT_APP_SUPABASE_IMAGE_URL + studentID.png
    let url = `${supabaseUrl}${imagePath}${user.studentID}.png`;
    
    // Add cache-busting query parameter if refreshKey is provided
    if (refreshKey > 0) {
      url += `?t=${refreshKey}&r=${Date.now()}`;
    }
    
    return url;
  }, [user?.studentID, refreshKey]);

  // Generate fallback URL with memoization
  const fallbackSrc = useMemo(() => {
    const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
    const imagePath = process.env.REACT_APP_SUPABASE_IMAGE_URL;
    
    if (!supabaseUrl || !imagePath) {
      return null;
    }
    
    return `${supabaseUrl}${imagePath}user.png`;
  }, []);

  // Handle image error - only set error state
  const handleImageError = useCallback(() => {
    setHasError(true);
  }, []);

  // Determine the final image source - use fallback if error, otherwise use imageSrc
  const finalImageSrc = hasError ? fallbackSrc : imageSrc;

  return (
    <Box sx={{ position: 'relative', display: 'inline-block' }}>
      <Avatar
        src={finalImageSrc}
        alt={user?.name || 'User'}
        onError={handleImageError}
        sx={{
          width: size,
          height: size,
          mx: 'auto',
          border: '3px solid #e9ecef',
          boxShadow: '0 4px 16px rgba(0,0,0,0.1)',
          backgroundColor: '#f5f5f5',
          ...sx
        }}
      >
        {!finalImageSrc && user?.name ? user.name.charAt(0).toUpperCase() : null}
      </Avatar>
    </Box>
  );
});

SupabaseImageAvatar.displayName = 'SupabaseImageAvatar';

export default SupabaseImageAvatar;
